/**
 * Sprint 232 — InfluencerCard
 *
 * Premium, fully-clickable card for the Influencer Discovery marketplace.
 *
 *   ┌─────────────────────────────────┐
 *   │  HERO: photo or video poster    │
 *   │  + intro-video PLAY badge       │
 *   │  + Heart (favorite) top-right   │
 *   │  + Verified ✓ top-left           │
 *   ├─────────────────────────────────┤
 *   │  Name · City · Tier             │
 *   │  Followers · ER · Avg views     │
 *   │  Category chips                 │
 *   │  Worked-with brand chips        │
 *   │  Portfolio thumbs (swipe)       │
 *   └─────────────────────────────────┘
 */
import React, { useState, useRef } from 'react';
import {
  Heart, CheckCircle2, Play, Users, TrendingUp, Eye, MapPin,
  Instagram, Music2, Youtube, Send, GitCompare,
} from 'lucide-react';

const TIER_LABEL = {
  ugc: 'UGC Creator',
  micro: 'Micro',
  mid: 'Mid-Tier',
  macro: 'Macro',
  mega: 'Mega',
  nano: 'Nano',     // legacy
};

const TIER_GLOW = {
  ugc:   'from-emerald-400/30 to-cyan-400/0',
  micro: 'from-sky-400/30 to-cyan-400/0',
  mid:   'from-violet-400/30 to-cyan-400/0',
  macro: 'from-amber-400/30 to-cyan-400/0',
  mega:  'from-fuchsia-500/40 to-cyan-400/0',
};

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const PLATFORM_ICONS = { instagram: Instagram, tiktok: Music2, youtube: Youtube };
const PLATFORM_LABELS = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube' };

const InfluencerCard = ({ influencer, onOpen, onToggleFav, isFav, onInvite, onCompare, inCompare }) => {
  const i = influencer;
  const followers = i.follower_count || i.total_reach || 0;
  const tierLabel = TIER_LABEL[i.influencer_tier] || (i.influencer_tier ? i.influencer_tier : null);
  const tierGlow = TIER_GLOW[i.influencer_tier] || 'from-cyan-400/20 to-transparent';
  const portfolio = (i.portfolio_media || []).slice(0, 6);
  const brands = (i.worked_with_brands || []).slice(0, 4);
  const hasVideo = Boolean(i.intro_video_upload_url || i.intro_video_url);
  const hero = i.photo;
  // Sprint 233.A — per-platform social accounts shown as colour chips
  const socials = (i.social_accounts || []).filter(s => s.handle);
  const hasAnyVerified = socials.some(s => s.is_verified_connection);

  // Swipe state for portfolio strip
  const stripRef = useRef(null);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen?.()}
      className="group relative rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden cursor-pointer transition-all hover:border-[#0DEAE3]/40 hover:shadow-[0_8px_40px_-8px_rgba(13,234,227,0.35)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#0DEAE3]/50"
      data-testid={`influencer-card-${i.user_id}`}
    >
      {/* Hero */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[#0F1620]">
        {hero ? (
          <img
            src={hero}
            alt={i.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[#101820] to-[#0B0F14]">
            <Users className="w-10 h-10 text-white/20" />
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/40 to-transparent" />

        {/* Top-left: Verified */}
        {i.is_verified && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-[#0DEAE3]/15 backdrop-blur-md border border-[#0DEAE3]/40 text-[11px] font-semibold text-[#0DEAE3]">
            <CheckCircle2 className="w-3 h-3" /> Verified
          </div>
        )}

        {/* Top-right: Favorite */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFav?.(); }}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center backdrop-blur-md transition-all border ${
            isFav
              ? 'bg-[#FF2D95] text-white border-[#FF2D95]'
              : 'bg-black/40 text-white/80 border-white/10 hover:bg-black/60 hover:text-white'
          }`}
          data-testid={`fav-btn-${i.user_id}`}
          aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart className={`w-4 h-4 ${isFav ? 'fill-white' : ''}`} />
        </button>

        {/* Intro video play badge */}
        {hasVideo && (
          <div
            className="absolute left-3 bottom-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-md border border-white/15 text-[11px] font-medium text-white"
            data-testid="intro-video-badge"
          >
            <Play className="w-3 h-3 fill-[#0DEAE3] text-[#0DEAE3]" /> Intro Video
          </div>
        )}

        {/* Tier badge */}
        {tierLabel && (
          <div className={`absolute right-3 bottom-3 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide bg-gradient-to-r ${tierGlow} bg-[#0B0F14]/70 backdrop-blur-md border border-white/15 text-white`}>
            {tierLabel}
          </div>
        )}

        {/* Name strip */}
        <div className="absolute left-4 right-4 bottom-12 pointer-events-none">
          <h3 className="text-xl font-bold leading-tight text-white drop-shadow-sm" data-testid="card-name">
            {i.name}
          </h3>
          {(i.city || i.state) && (
            <p className="text-[12px] text-white/70 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {[i.city, i.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Iter-252 — Creator Type chip (Influencer / Content Creator / Both) */}
        {i.creator_type && i.creator_type !== 'creator' && (
          <div className="-mt-1 flex items-center gap-1.5" data-testid="creator-type-chip">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                i.creator_type === 'both'
                  ? 'bg-gradient-to-r from-violet-500/20 to-pink-500/20 text-violet-200 border-violet-400/40'
                  : i.creator_type === 'influencer'
                  ? 'bg-pink-500/15 text-pink-200 border-pink-400/40'
                  : 'bg-purple-500/15 text-purple-200 border-purple-400/40'
              }`}
            >
              {i.creator_type === 'both' ? 'Influencer + Creator' :
               i.creator_type === 'influencer' ? 'Influencer' : 'Content Creator'}
            </span>
          </div>
        )}
        {/* Stats */}
        <div className="grid grid-cols-3 gap-1 text-center">
          <Stat icon={Users} label="Followers" value={formatCount(followers)} />
          <Stat icon={TrendingUp} label="ER" value={i.engagement_rate != null ? `${Number(i.engagement_rate).toFixed(1)}%` : '—'} />
          <Stat icon={Eye} label="Avg Views" value={formatCount(i.average_views)} />
        </div>

        {/* Categories */}
        {(i.creator_categories || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {i.creator_categories.slice(0, 3).map(c => (
              <span
                key={c}
                className="px-2 py-0.5 rounded-full text-[10.5px] font-medium bg-white/5 border border-white/10 text-white/75"
              >
                {c}
              </span>
            ))}
            {i.creator_categories.length > 3 && (
              <span className="px-2 py-0.5 rounded-full text-[10.5px] text-white/40">
                +{i.creator_categories.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Worked With */}
        {brands.length > 0 && (
          <div data-testid="worked-with">
            <p className="text-[10.5px] uppercase tracking-wider text-white/40 mb-1">Worked With</p>
            <div className="flex flex-wrap gap-1">
              {brands.map(b => (
                <span
                  key={b.brand_name}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-[#0DEAE3]/8 border border-[#0DEAE3]/25 text-[#A5F8F4]"
                >
                  {b.brand_logo_url && (
                    <img src={b.brand_logo_url} alt="" className="w-3 h-3 rounded-sm object-cover" />
                  )}
                  {b.brand_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio strip */}
        {portfolio.length > 0 && (
          <div className="-mx-1" data-testid="portfolio-strip">
            <div
              ref={stripRef}
              className="flex gap-1 overflow-x-auto px-1 pb-1 snap-x snap-mandatory scrollbar-thin"
              onClick={(e) => e.stopPropagation()}
              onWheel={(e) => {
                // Convert vertical wheel into horizontal scroll for desktop
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                  stripRef.current.scrollLeft += e.deltaY;
                }
              }}
            >
              {portfolio.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`Portfolio ${idx + 1}`}
                  loading="lazy"
                  className="h-16 w-16 rounded-md object-cover flex-shrink-0 border border-white/5 snap-start"
                />
              ))}
            </div>
          </div>
        )}

        {/* Sprint 233.A — Social platform chips with platform-level metrics */}
        {socials.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="social-chips">
            {socials.map((s) => {
              const Icon = PLATFORM_ICONS[s.platform];
              const verified = s.is_verified_connection;
              return (
                <span
                  key={s.platform}
                  title={`${PLATFORM_LABELS[s.platform]} · ${s.handle} · ${verified ? 'Verified' : 'Self-reported'}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${
                    verified
                      ? 'bg-[#0DEAE3]/10 border-[#0DEAE3]/40 text-[#A5F8F4]'
                      : 'bg-white/5 border-white/10 text-white/70'
                  }`}
                  data-testid={`social-chip-${s.platform}`}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  {formatCount(s.follower_count || 0)}
                  {!verified && <span className="text-amber-300/80 text-[9px] uppercase tracking-wider">SR</span>}
                </span>
              );
            })}
          </div>
        )}

        {/* Sprint 233.B — Action row (Save / Invite / Compare) */}
        <div
          className="flex items-center gap-1.5 pt-1"
          onClick={(e) => e.stopPropagation()}
          data-testid="card-actions"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFav?.(); }}
            className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition ${
              isFav
                ? 'bg-[#FF2D95]/15 text-[#FF6BB5] border-[#FF2D95]/30'
                : 'bg-white/5 text-white/75 border-white/10 hover:border-white/25'
            }`}
            data-testid="save-btn"
          >
            <Heart className={`w-3 h-3 ${isFav ? 'fill-current' : ''}`} /> {isFav ? 'Saved' : 'Save'}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onInvite?.(i); }}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border bg-[#0DEAE3]/10 text-[#0DEAE3] border-[#0DEAE3]/30 hover:bg-[#0DEAE3]/20"
            data-testid="invite-btn"
          >
            <Send className="w-3 h-3" /> Invite
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCompare?.(i); }}
            className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition ${
              inCompare
                ? 'bg-violet-500/20 text-violet-200 border-violet-400/40'
                : 'bg-white/5 text-white/75 border-white/10 hover:border-white/25'
            }`}
            data-testid="compare-btn"
          >
            <GitCompare className="w-3 h-3" /> {inCompare ? 'In Compare' : 'Compare'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-1 py-2">
    <Icon className="w-3 h-3 text-[#0DEAE3] mx-auto mb-0.5" />
    <p className="text-sm font-bold text-white leading-none">{value}</p>
    <p className="text-[9.5px] uppercase tracking-wider text-white/40 mt-1">{label}</p>
  </div>
);

export default InfluencerCard;
